import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Loader2, MapPin } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { getTeamSettings, setDefaultDepot, updateTeamSettings, type TeamSettings } from "@/services/team";
import { listDepots } from "@/services/depots";

/**
 * Settings → Organization. Admin / Route Manager edit team-level config:
 *   - Name + description
 *   - Working-hours window (informational; future: planner validation)
 *   - Timezone (used by Activity log + dashboard time-window labels)
 *   - Default depot (flips depots.is_default for the chosen depot — Plan
 *     page reads this when seeding a new route)
 *
 * Persistence: direct UPDATE on the `teams` row (admin-gated by RLS).
 * Realtime: every UPDATE fans out via supabase_realtime → other admin tabs
 * invalidate ["team", "settings"] and re-render with the new values.
 */
export function SettingsOrganization() {
  const me = useAuthStore((s) => s.user);
  const teamId = me?.teamId ?? null;
  const qc = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: ["team", "settings", teamId],
    queryFn: () => (teamId ? getTeamSettings(teamId) : Promise.resolve(null)),
    enabled: !!teamId,
  });
  const depotsQuery = useQuery({ queryKey: ["depots"], queryFn: listDepots });

  // Local form state mirrors the server row. Initialized from the query and
  // re-synced via effect on every fetch so realtime updates from other tabs
  // refresh the form too (unless the user is mid-edit — we mark dirty).
  const [draft, setDraft] = useState<TeamSettings | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    // Re-seed the form when the server data changes AND the user hasn't
    // started editing. Without the `dirty` guard, a realtime update would
    // wipe in-flight edits — frustrating during concurrent admin work.
    if (settingsQuery.data && !dirty) {
      setDraft(settingsQuery.data);
    }
  }, [settingsQuery.data, dirty]);

  const defaultDepotId = useMemo(
    () => (depotsQuery.data ?? []).find((d) => d.is_default)?.id ?? null,
    [depotsQuery.data],
  );

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!teamId || !draft) throw new Error("Not ready");
      await updateTeamSettings(teamId, {
        name: draft.name,
        description: draft.description,
        working_hours_start: normalizeTime(draft.working_hours_start),
        working_hours_end: normalizeTime(draft.working_hours_end),
        timezone: draft.timezone,
      });
    },
    onSuccess: () => {
      toast.success("Organization settings saved");
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["team", "settings"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const depotMut = useMutation({
    mutationFn: async (depotId: string) => {
      if (!teamId) throw new Error("Not ready");
      await setDefaultDepot(teamId, depotId);
    },
    onSuccess: () => {
      toast.success("Default depot updated");
      qc.invalidateQueries({ queryKey: ["depots"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (settingsQuery.isLoading || !draft) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading organization settings…
      </div>
    );
  }

  if (settingsQuery.error) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
        Couldn't load organization settings: {(settingsQuery.error as Error).message}
      </div>
    );
  }

  function patch<K extends keyof TeamSettings>(key: K, value: TeamSettings[K]) {
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
    setDirty(true);
  }

  const depots = depotsQuery.data ?? [];

  return (
    <div className="max-w-2xl space-y-4">
      {/* ----- Team identity ----- */}
      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <h3 className="text-sm font-semibold text-slate-900">Team identity</h3>
        <p className="mt-1 text-xs text-slate-500">
          Name + description shown to members and in route exports.
        </p>
        <div className="mt-4 grid gap-3">
          <label className="block text-xs font-medium text-slate-600">
            Team name
            <input
              type="text"
              value={draft.name}
              maxLength={120}
              onChange={(e) => patch("name", e.target.value)}
              className="mt-1 block w-full rounded-md border-slate-300 px-3 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
            />
          </label>
          <label className="block text-xs font-medium text-slate-600">
            Description <span className="text-slate-400">(optional)</span>
            <textarea
              value={draft.description ?? ""}
              maxLength={500}
              onChange={(e) => patch("description", e.target.value || null)}
              rows={2}
              className="mt-1 block w-full rounded-md border-slate-300 px-3 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
              placeholder="A short description visible to the team"
            />
          </label>
        </div>
      </section>

      {/* ----- Working hours + timezone ----- */}
      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <h3 className="text-sm font-semibold text-slate-900">Working hours</h3>
        <p className="mt-1 text-xs text-slate-500">
          When the team is actively in the field. Used by route planning + activity-log time displays.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <label className="block text-xs font-medium text-slate-600">
            Start
            <input
              type="time"
              value={toTimeInput(draft.working_hours_start)}
              onChange={(e) => patch("working_hours_start", e.target.value)}
              className="mt-1 block w-full rounded-md border-slate-300 px-3 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
            />
          </label>
          <label className="block text-xs font-medium text-slate-600">
            End
            <input
              type="time"
              value={toTimeInput(draft.working_hours_end)}
              onChange={(e) => patch("working_hours_end", e.target.value)}
              className="mt-1 block w-full rounded-md border-slate-300 px-3 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
            />
          </label>
          <label className="block text-xs font-medium text-slate-600">
            Timezone
            <select
              value={draft.timezone}
              onChange={(e) => patch("timezone", e.target.value)}
              className="mt-1 block w-full rounded-md border-slate-300 px-3 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
            >
              {TIMEZONE_OPTIONS.map((tz) => (
                <option key={tz} value={tz}>
                  {tz.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {/* ----- Default depot ----- */}
      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <h3 className="text-sm font-semibold text-slate-900">Default depot</h3>
        <p className="mt-1 text-xs text-slate-500">
          New routes start from this depot unless the planner overrides it.
        </p>
        {depotsQuery.isLoading ? (
          <p className="mt-4 text-sm text-slate-500">Loading depots…</p>
        ) : depots.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500">
            No depots yet. Add one from the route planner — it'll show up here.
          </p>
        ) : (
          <ul className="mt-4 divide-y divide-slate-100 rounded-md border border-slate-200">
            {depots.map((d) => {
              const isDefault = d.id === defaultDepotId;
              return (
                <li key={d.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <MapPin className="h-4 w-4 shrink-0 text-brand-600" />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-slate-900">
                        {d.name}
                        {isDefault && (
                          <span className="ml-2 rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-700">
                            Default
                          </span>
                        )}
                      </div>
                      <div className="truncate text-xs text-slate-500">
                        {[d.address_street, d.address_city, d.address_state, d.address_zip].filter(Boolean).join(", ") || "—"}
                      </div>
                    </div>
                  </div>
                  {!isDefault && (
                    <button
                      type="button"
                      onClick={() => depotMut.mutate(d.id)}
                      disabled={depotMut.isPending}
                      className="shrink-0 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      Set as default
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ----- Save ----- */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-4">
        <p className="text-xs text-slate-500">
          {dirty
            ? "You have unsaved changes."
            : "Changes save when you click below; other admin tabs update live via realtime."}
        </p>
        <div className="flex items-center gap-2">
          {dirty && (
            <button
              type="button"
              onClick={() => {
                if (settingsQuery.data) setDraft(settingsQuery.data);
                setDirty(false);
              }}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Discard
            </button>
          )}
          <button
            type="button"
            onClick={() => saveMut.mutate()}
            disabled={!dirty || saveMut.isPending}
            className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {saveMut.isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- helpers --------------------------------------------------------------

// Postgres time type may serialize as 'HH:MM:SS'; HTML <input type="time">
// expects 'HH:MM'. Drop the seconds without losing precision (we never set
// seconds anyway).
function toTimeInput(s: string): string {
  return s?.length >= 5 ? s.slice(0, 5) : s ?? "";
}
function normalizeTime(s: string): string {
  // Re-canonicalize what came back out of the <input type="time"> into the
  // Postgres-friendly 'HH:MM:SS' form.
  if (!s) return "00:00:00";
  return s.length === 5 ? `${s}:00` : s;
}

// IANA timezones supported in the dropdown. Trimmed to common US zones + a
// few international ones to keep the list short. A team in an exotic zone
// can be added when the need surfaces — we don't enumerate the full 600+
// IANA list because that's its own UX problem.
const TIMEZONE_OPTIONS = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Toronto",
  "America/Mexico_City",
  "Europe/London",
  "UTC",
];
